/**
 * CalendarEventPill — one selectable workshop event inside a calendar day cell
 * (shared {@link WorkshopCalendarPicker}). Tree-type color dot + title + subtitle
 * + seats hint. Selected events are visually distinct; disabled events are shown
 * but not selectable (the disabled reason becomes the tooltip).
 */
import { useState } from 'react';

import { Tooltip } from '../Tooltip';

import type { CalendarEvent, CalendarEventTooltip } from './calendarEvent';
import { dotColor } from './calendarEvent';

export interface CalendarEventPillProps {
  event: CalendarEvent;
  selected: boolean;
  onSelect: (id: string) => void;
}

/** Tooltip body — the fields the truncated in-cell pill can't show in full:
 *  workshop/tree name, venue, date, local time, tree species, seats-left,
 *  price (BON-142). */
export function CalendarEventTooltipBody({ tooltip }: { tooltip: CalendarEventTooltip }) {
  return (
    <div className="space-y-0.5">
      <p className="font-semibold text-bark-900 dark:text-stone-100">{tooltip.title}</p>
      <p>{tooltip.venueName}</p>
      <p>
        {tooltip.dateLabel} &middot; {tooltip.timeLabel}
      </p>
      {tooltip.treeSpeciesName && <p>{tooltip.treeSpeciesName}</p>}
      <p>
        {tooltip.seatsLabel} &middot; {tooltip.priceLabel}
      </p>
    </div>
  );
}

/** Pill background — a SIMPLE hover tint (paired with a short transition on the
 *  button), NOT the always-on gray box the pill used to carry (BON-142 — the
 *  "oddly applied gradient/shading" was that resting box + the tooltip shadow).
 *  Resting pills are transparent so events read cleanly; selected keeps the moss
 *  tint (+ a moss border on the button). */
function pillBackground(selected: boolean, hover: boolean, disabled: boolean): string {
  if (selected) return 'var(--nb-primary-soft, rgba(14,135,75,.10))';
  if (hover && !disabled) return 'var(--nb-bg-2, rgba(0,0,0,.05))';
  return 'transparent';
}

/** The pill's inner markup (color dot + title, subtitle, seats line). Extracted
 *  so `CalendarEventPill` stays under the per-function line cap. */
function PillContent({ event, color }: { event: CalendarEvent; color: string }) {
  return (
    <>
      <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
        <i
          aria-hidden="true"
          style={{ flex: '0 0 auto', width: 7, height: 7, borderRadius: '50%', background: color }}
        />
        <span
          style={{
            fontSize: 10.5,
            fontWeight: 600,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {event.title}
        </span>
      </span>
      <span
        style={{
          display: 'block',
          fontSize: 9.5,
          color: 'var(--nb-muted)',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          marginTop: 1,
        }}
      >
        {event.subtitle}
      </span>
      <SeatsLine event={event} />
    </>
  );
}

export function CalendarEventPill({ event, selected, onSelect }: CalendarEventPillProps) {
  const color = dotColor(event.title);
  const [hover, setHover] = useState(false);
  const background = pillBackground(selected, hover, event.disabled);
  const pill = (
    <button
      type="button"
      role="option"
      aria-selected={selected}
      disabled={event.disabled}
      title={event.disabled ? event.disabledReason : undefined}
      onClick={() => onSelect(event.id)}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'block',
        width: '100%',
        textAlign: 'left',
        padding: '4px 6px',
        borderRadius: 6,
        border: `1px solid ${selected ? 'var(--nb-primary)' : 'transparent'}`,
        background,
        transition: 'background-color 120ms ease',
        cursor: event.disabled ? 'not-allowed' : 'pointer',
        opacity: event.disabled ? 0.5 : 1,
        font: 'inherit',
        color: 'var(--nb-ink)',
        marginBottom: 3,
      }}
    >
      <PillContent event={event} color={color} />
    </button>
  );

  if (!event.tooltip) return pill;
  const content = <CalendarEventTooltipBody tooltip={event.tooltip} />;
  return (
    <Tooltip content={content} className="block w-full">
      {pill}
    </Tooltip>
  );
}

/** Seats hint, or the disabled reason when the event can't take the move. */
function SeatsLine({ event }: { event: CalendarEvent }) {
  if (!event.seatsLabel) return null;
  return (
    <span
      style={{
        display: 'block',
        fontSize: 9.5,
        color: event.disabled ? 'var(--nb-warn)' : 'var(--nb-muted)',
        marginTop: 1,
      }}
    >
      {event.disabled ? (event.disabledReason ?? event.seatsLabel) : event.seatsLabel}
    </span>
  );
}
